import { BareActionContext, getStoreBuilder } from 'vuex-typex';
import axios from 'axios';
import { RootState } from '@/store/state';
import { BannersState } from './state';
import appStore from '@/store/app';
import { Api, ApiResult, BannerPosition, BannerImage, SingleResultOf, ResultOf, Guid, SelectItem } from '@/types';
import { Banner } from '@/models';
import { formatDate } from '@/filters/index';
import { addDays } from '@/filters';

type StoreContext = BareActionContext<BannersState, RootState>;

const createEmptyBanner: () => Banner = () => {
   return {
      id: Guid.Empty,
      name: '',
      publishingDate: new Date(),
      expiryDate: addDays(new Date(), 30),
      fileName: '',
      originalFileName: '',
      position: BannerPosition.Left,
      hyperLink: '',
   } as Banner;
};

const thisState: BannersState = {
   total: 0,
   banners: [],
   banner: undefined,
   currentBanners: [],
   currentBannersLoaded: false,
   positions: [
      { value: BannerPosition.Left.toString(), description: 'Sinistra' } as SelectItem<string>,
      { value: BannerPosition.Center.toString(), description: 'Centro' } as SelectItem<string>,
      { value: BannerPosition.Right.toString(), description: 'Destra' } as SelectItem<string>,
   ],
};

const moduleBuilder = getStoreBuilder<RootState>().module('banners', thisState);

const thisModule = {
   getters: {
      banners(state: BannersState): Banner[] {
         return state.banners;
      },

      total(state: BannersState): number {
         return state.total;
      },

      banner(state: BannersState): Banner {
         return state.banner || createEmptyBanner();
      },

      positions(state: BannersState): SelectItem<string>[] {
         return state.positions;
      },

      currentBannersLoaded(state: BannersState): boolean {
         return state.currentBannersLoaded;
      },

      emptyBanner(state: BannersState): Banner {
         return createEmptyBanner();
      },

      bannerLeft(state: BannersState): Banner | undefined {
         if (!state.currentBanners) return undefined;
         const displayBanner = state.currentBanners.filter((banner) => banner.position === BannerPosition.Left);
         if (!displayBanner || displayBanner.length === 0) {
            return undefined;
         }
         return displayBanner[0];
      },

      bannerCenter(state: BannersState): Banner | undefined {
         if (!state.currentBanners) return undefined;
         const displayBanner = state.currentBanners.filter((banner) => banner.position === BannerPosition.Center);
         if (!displayBanner || displayBanner.length === 0) {
            return undefined;
         }
         return displayBanner[0];
      },

      bannerRight(state: BannersState): Banner | undefined {
         if (!state.currentBanners) return undefined;
         const displayBanner = state.currentBanners.filter((banner) => banner.position === BannerPosition.Right);
         if (!displayBanner || displayBanner.length === 0) {
            return undefined;
         }
         return displayBanner[0];
      },
   },

   actions: {

      async fetchBannersAsync(context: StoreContext, payload: { fromDate: Date, toDate: Date, pageNumber: number, pageSize: number }): Promise<void> {

         appStore.loading({ isLoading: true });

         const fromDate = formatDate(payload.fromDate, 'yyyy-MM-dd');
         const toDate = formatDate(payload.toDate, 'yyyy-MM-dd');

         return await axios.get<ResultOf<Banner>>(Api.BaseUrl + `banners/search/${fromDate}/${toDate}`, {
            params: {
               page: payload.pageNumber,
               size: payload.pageSize,
            },
         })
            .then((result) => {
               commitPopulateBanners({ total: result.data.total, banners: result.data.payload });
            })
            .catch((reason) => {
               if (reason && reason.response) {
                  appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: true });
               }
               commitPopulateBanners({ total: 0, banners: [] });
            })
            .finally(() => {
               appStore.loading({ isLoading: false });
            });
      },

      async fetchCurrentBannersAsync(context: StoreContext): Promise<void> {

         appStore.loading({ isLoading: true });

         return await axios.get<ResultOf<Banner>>(Api.BaseUrl + `banners/current`)
            .then((result) => {
               commitPopulateCurrentBanners({ banners: result.data.payload });
            })
            .catch((reason) => {
               if (reason && reason.response) {
                  appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: true });
               }
               commitPopulateCurrentBanners({ banners: [] });
            })
            .finally(() => {
               appStore.loading({ isLoading: false });
            });
      },

      async fetchBannerAsync(context: StoreContext, payload: { id: string }): Promise<void> {

         if (!payload || payload.id === Guid.Empty) {
            commitPopulateBanner(createEmptyBanner());
            return;
         }

         appStore.loading({ isLoading: true });

         return await axios.get<SingleResultOf<Banner>>(Api.BaseUrl + `banners/${payload.id}`)
            .then((result) => {
               commitPopulateBanner(result.data.payload || createEmptyBanner());
            })
            .catch((reason) => {
               if (reason && reason.response) {
                  appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: false });
               }
               commitPopulateBanner(createEmptyBanner());
            })
            .finally(() => {
               appStore.loading({ isLoading: false });
            });
      },

      async upsertBannerAsync(context: StoreContext, payload: {
         id: string, name: string, publishingDate: Date, expiryDate: Date, position: BannerPosition,
         fileName: string, originalFileName: string, hyperLink?: string
      }): Promise<boolean> {

         if (!payload) { return false; }

         appStore.loading({ isLoading: true });

         const isNew = payload.id === Guid.Empty;

         return await axios.post<SingleResultOf<string>>(Api.BaseUrl + `banners`, payload)
            .then((response) => {
               if (isNew) {
                  payload.id = response.data.payload || Guid.Empty;
                  commitInsertBanner(payload);
               } else {
                  commitUpdateBanner(payload);
               }
               return true;
            })
            .catch((reason) => {
               if (reason && reason.response) {
                  appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: false });
               }
               return false;
            })
            .finally(() => {
               appStore.loading({ isLoading: false });
            });
      },

      async deleteBannerAsync(context: StoreContext, payload: { id: string }): Promise<boolean> {

         if (!payload) { return false; }

         appStore.loading({ isLoading: true });

         return await axios.delete(Api.BaseUrl + `banners/${payload.id}`)
            .then((result) => {
               commitDeleteBanner(payload);
               appStore.success({ message: 'Il banner è stato eliminato con successo.' });
               return true;
            })
            .catch((reason) => {
               if (reason && reason.response) {
                  appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: false });
               }
               return false;
            })
            .finally(() => {
               appStore.loading({ isLoading: false });
            });
      },

      async uploadBannerAsync(context: StoreContext, payload: { file: File }): Promise<BannerImage | undefined> {

         if (!payload) { return undefined; }

         appStore.loading({ isLoading: true });

         const formData = new FormData();

         formData.append('request', payload.file);

         return await axios.post<SingleResultOf<BannerImage>>(Api.BaseUrl + `resources`, formData, {
            // headers: {
            //    'Content-Type': 'multipart/form-data',
            // },
         })
            .then((response) => {
               return response.data.payload;
            })
            .catch((reason) => {
               if (reason && reason.response) {
                  appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: false });
               }
               return undefined;
            })
            .finally(() => {
               appStore.loading({ isLoading: false });
            });
      },

   },

   mutations: {

      populateBanners(state: BannersState, payload: { banners: Banner[], total: number }) {
         state.banners = payload.banners;
         state.total = payload.total;
      },

      populateCurrentBanners(state: BannersState, payload: { banners: Banner[] }) {
         state.currentBannersLoaded = true;
         state.currentBanners = payload.banners;
      },

      populateBanner(state: BannersState, payload: Banner) {
         state.banner = payload;
      },

      deleteBanner(state: BannersState, payload: { id: string }) {
         if (state.banners) {
            const index = state.banners.findIndex((banner) => banner.id === payload.id);
            if (index > -1) {
               state.banners.splice(index, 1);
            }
         }
         if (state.total > 0) {
            state.total--;
         }
      },

      insertBanner(state: BannersState, payload: {
         id: string, name: string, publishingDate: Date, expiryDate: Date,
         position: BannerPosition, fileName: string, originalFileName: string, hyperLink?: string
      }) {

         const positionDescription = state.positions.filter((p) => p.value === payload.position.toString())[0].description;

         const banner: Banner = {
            id: payload.id,
            name: payload.name,
            publishingDate: payload.publishingDate,
            expiryDate: payload.expiryDate,
            position: payload.position,
            positionDescription,
            fileName: payload.fileName,
            originalFileName: payload.originalFileName,
            hyperLink: payload.hyperLink,
         };
         state.banners.unshift(banner);
         state.total++;

         state.currentBannersLoaded = false;
      },

      updateBanner(state: BannersState, payload: {
         id: string, name: string, publishingDate: Date, expiryDate: Date,
         position: BannerPosition, fileName: string, originalFileName: string, hyperLink?: string
      }) {

         const index = state.banners.findIndex((x) => x.id === payload.id);

         if (index > -1) {

            const positionDescription = state.positions.filter((p) => p.value === payload.position.toString())[0].description;

            const updated = state.banners[index];
            updated.name = payload.name;
            updated.publishingDate = payload.publishingDate;
            updated.expiryDate = payload.expiryDate;
            updated.position = payload.position;
            updated.positionDescription = positionDescription;
            updated.fileName = payload.fileName;
            updated.originalFileName = payload.originalFileName;
            updated.hyperLink = payload.hyperLink;
            state.banners.splice(index, 1, updated);
         }

         if (state.currentBanners && state.currentBanners.length > 0) {
            const indexFound = state.currentBanners.findIndex((x) => x.id === payload.id);
            if (indexFound > -1) {
               const currentUpdated = state.currentBanners[indexFound];
               currentUpdated.fileName = payload.fileName;
               currentUpdated.originalFileName = payload.originalFileName;
               currentUpdated.hyperLink = payload.hyperLink;
               currentUpdated.url = `/content/banners/${payload.fileName}`;
               state.currentBanners.splice(indexFound, 1, currentUpdated);
            }
         }
         state.currentBannersLoaded = false;
      },
   },
};

// private getters
const getBanners = moduleBuilder.read(thisModule.getters.banners, 'getBanners');
const getTotalBanners = moduleBuilder.read(thisModule.getters.total, 'getTotalBanners');
const getBanner = moduleBuilder.read(thisModule.getters.banner, 'getBanner');
const getPositions = moduleBuilder.read(thisModule.getters.positions, 'getPositions');
const getBannerLeft = moduleBuilder.read(thisModule.getters.bannerLeft, 'getBannerLeft');
const getBannerCenter = moduleBuilder.read(thisModule.getters.bannerCenter, 'getBannerCenter');
const getBannerRight = moduleBuilder.read(thisModule.getters.bannerRight, 'getBannerRight');
const getCurrentBannersLoaded = moduleBuilder.read(thisModule.getters.currentBannersLoaded, 'getCurrentBannersLoaded');
const getEmptyBanner = moduleBuilder.read(thisModule.getters.emptyBanner, 'getEmptyBanner');

// mutations
const commitPopulateBanners = moduleBuilder.commit(thisModule.mutations.populateBanners, 'commitPopulateBanners');
const commitPopulateCurrentBanners = moduleBuilder.commit(thisModule.mutations.populateCurrentBanners, 'commitPopulateCurrentBanners');
const commitPopulateBanner = moduleBuilder.commit(thisModule.mutations.populateBanner, 'commitPopulateBanner');
const commitDeleteBanner = moduleBuilder.commit(thisModule.mutations.deleteBanner, 'commitDeleteBanner');
const commitUpdateBanner = moduleBuilder.commit(thisModule.mutations.updateBanner, 'commitUpdateBanner');
const commitInsertBanner = moduleBuilder.commit(thisModule.mutations.insertBanner, 'commitInsertBanner');

const banners = {

   // getters (wrapped as real getters)
   get banners() { return getBanners(); },
   get total() { return getTotalBanners(); },
   get banner() { return getBanner(); },
   get positions() { return getPositions(); },
   get bannerLeft() { return getBannerLeft(); },
   get bannerCenter() { return getBannerCenter(); },
   get bannerRight() { return getBannerRight(); },
   get currentBannersLoaded() { return getCurrentBannersLoaded(); },
   get emptyBanner() { return getEmptyBanner(); },

   // actions
   fetchBannersAsync: moduleBuilder.dispatch(thisModule.actions.fetchBannersAsync, 'fetchBannersAsync'),
   fetchCurrentBannersAsync: moduleBuilder.dispatch(thisModule.actions.fetchCurrentBannersAsync, 'fetchCurrentBannersAsync'),
   fetchBannerAsync: moduleBuilder.dispatch(thisModule.actions.fetchBannerAsync, 'fetchBannerAsync'),
   upsertBannerAsync: moduleBuilder.dispatch(thisModule.actions.upsertBannerAsync, 'upsertBannerAsync'),
   deleteBannerAsync: moduleBuilder.dispatch(thisModule.actions.deleteBannerAsync, 'deleteBannerAsync'),
   uploadBannerAsync: moduleBuilder.dispatch(thisModule.actions.uploadBannerAsync, 'uploadBannerAsync'),
};

export default banners;
